import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import importIcon from "assets/images/svg/import.svg";
import exportIcon from "assets/images/svg/export.svg";
import { STO_TYPES } from "pages/stock-management/stockTransferOrders/constants";
import { textFormatter } from "components/Common/formatter";
import STODetailsModal from "pages/stock-management/stockTransferOrders/STODetails/STODetailsModal";
import CustomLink from "components/Common/buttons/CustomLink";
import { Collapse, Divider, Pagination } from "antd";
import LoadedAssetItem from "pages/stock-management/trips/tripDetails/LoadedAssetItem";
import NGIDropdownButton from "components/Common/buttons/NGIDropdownButton";
import menuIcon from "assets/images/svg/MenuVertical.svg";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { updateTripSTO } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { userHasOneOfRoles } from "utils/helpers/functions";
import { USER_ROLES } from "utils/constants";
import classnames from "classnames";
import EditSTOModal from "pages/stock-management/trips/editTrip/editSTO/EditSTOModal";
import ConfirmationUnassignModal from "pages/stock-management/trips/editTrip/editSTO/ConfirmationUnassignModal";
import { UNASSIGN_CREATE_TYPE, UPDATE_REASONS } from "pages/stock-management/trips/editTrip/constants";
import { TRIP_STATUSES } from "pages/stock-management/trips/constants";
import ConfirmationUpdateModal from "pages/stock-management/trips/editTrip/editSTO/ConfirmationUpdateModal";

const useStyles = makeStyles({
  assetItem: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#f5f5f5",
    borderRadius: 50,
    padding: 9,
    paddingRight: 30,
  },
  changed: {
    backgroundColor: "#ecfaf0",

    "&> .ant-collapse-header": {
      backgroundColor: "#ecfaf0",
      borderRadius: "30px!important",
    },
    "&> .ant-collapse-content": {
      backgroundColor: "#ecfaf0!important",
    },
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    marginRight: 20,
    background: "rgba(255, 255, 255, 1)",
  },
  subtitle: {
    marginRight: 10,
    color: "grey",
  },
  rightContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "50%",
    marginRight: 10,
  },
  outbound: {
    marginLeft: 3,
  },
  inbound: {
    marginRight: 3,
  },
  displayFlex: {
    display: "flex",
  },
  link: {
    fontWeight: 600,
    textDecoration: "underline",
  },
  collapse: {
    marginBottom: 10,
    border: "none",
    borderRadius: "30px",
    backgroundColor: "#f5f5f5",
    "&> .ant-collapse-item:last-child > .ant-collapse-header": {
      borderRadius: "0px 0px 30px 30px",
    },
    "&> .ant-collapse-item": {
      borderBottom: "none",
    },
  },
  collapsePanel: {
    borderRadius: "30px!important",

    "&> .ant-collapse-header": {
      padding: "0 !important",
      borderBottom: "none",
    },
    "&> .ant-collapse-content": {
      backgroundColor: "#f5f5f5",
      borderRadius: "0px 0px 30px 30px !important",
    },
  },
  description: {
    display: "flex",
    justifyContent: "center",
  },
  viewAll: {
    height: 49,
    display: "flex",
    alignItems: "center",
    marginRight: 13,
    textDecoration: "underline",
  },
  extraContainer: {
    display: "flex",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
});

const { Panel } = Collapse;

const EditAssetItem = ({
  asset,
  stop,
  trip,
  isChanged = false,
  loadedAssets,
  offloadedAssets,
  isEdit,
  update,
  setChangedList,
}) => {
  const classes = useStyles();
  const { stoType, group, condition, quantity, stoId, stoItemId } = asset;
  const [isLoading, setLoader] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpenType, setIsEditOpenType] = useState("");
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isInboundRemoveOpen, setIsInboundRemoveOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [showSize, setShowSize] = useState(10);
  const [pageOffload, setPageOffload] = useState(1);
  const [showSizeOffload, setShowSizeOffload] = useState(10);
  const [isOutboundUnassignOpen, setIsOutboundUnassignOpen] = useState(false);
  const [isInboundUnassignOpen, setIsInboundUnassignOpen] = useState(false);

  const openDetails = useCallback(e => {
    e.stopPropagation();
    setIsOpen(true);
  }, []);

  const closeDetails = useCallback(() => setIsOpen(false), []);

  const openEditSto = useCallback(
    isReCreate => setIsEditOpenType(isReCreate ? UNASSIGN_CREATE_TYPE.RECREATE : UNASSIGN_CREATE_TYPE.CREATE_NEW),
    []
  );
  const closeEditSto = useCallback(() => setIsEditOpenType(""), []);

  const openRemoveSto = useCallback(() => {
    switch (trip.status) {
      case TRIP_STATUSES.PLANNED:
      case TRIP_STATUSES.PENDING:
        const fn = stoType === STO_TYPES.INBOUND ? setIsInboundRemoveOpen : setIsRemoveOpen;
        fn(true);
        return;
      case TRIP_STATUSES.IN_TRANSIT:
        if (stoType === STO_TYPES.OUTBOUND) {
          setIsOutboundUnassignOpen(true);
        }
        if (stoType === STO_TYPES.INBOUND) {
          setIsInboundUnassignOpen(true);
        }
        return;
      default:
        break;
    }
  }, [trip, stoType]);
  const closeRemoveSto = useCallback(() => setIsRemoveOpen(false), []);
  const closeRemoveInboundSto = useCallback(() => setIsInboundRemoveOpen(false), []);
  const closeOutboundUnassignSto = useCallback(() => setIsOutboundUnassignOpen(false), []);
  const closeInboundUnassignSto = useCallback(() => setIsInboundUnassignOpen(false), []);

  const onConfirmOutboundUnassign = useCallback(() => {
    setIsOutboundUnassignOpen(false);
    setIsEditOpenType(UNASSIGN_CREATE_TYPE.OUTBOUND_DEDUCTION);
  }, []);

  const onConfirmInboundUnassign = useCallback(type => {
    setIsInboundUnassignOpen(false);
    setIsEditOpenType(type);
  }, []);

  const filterAssets = useCallback(
    (group, condition, assets) => assets?.filter(item => item.group === group && item.condition === condition),
    []
  );

  const filteredLoadedAssets = filterAssets(group, condition, loadedAssets);
  const filteredOffloadedAssets = filterAssets(group, condition, offloadedAssets);

  const loadedAssetsCount = useMemo(
    () => filteredLoadedAssets?.reduce((count, item) => count + item?.assets?.length, 0) || 0,
    [filteredLoadedAssets]
  );

  const offloadedAssetsCount = useMemo(
    () => filteredOffloadedAssets?.reduce((count, item) => count + item?.assets?.length, 0) || 0,
    [filteredOffloadedAssets]
  );

  const paginatedLoadedAssets = useMemo(
    () =>
      filteredLoadedAssets
        ?.reduce((items, item) => [...items, ...(item?.assets || [])], [])
        ?.slice((page - 1) * showSize, page * showSize),
    [filteredLoadedAssets, page, showSize]
  );

  const paginatedOffloadedAssets = useMemo(
    () =>
      filteredOffloadedAssets
        ?.reduce((items, item) => [...items, ...(item?.assets || [])], [])
        ?.slice((pageOffload - 1) * showSizeOffload, pageOffload * showSizeOffload),
    [filteredOffloadedAssets, pageOffload, showSizeOffload]
  );

  const applyPage = useCallback(page => setPage(page), []);
  const applyPagSize = useCallback((curent, size) => setShowSize(size), []);

  const applyOffloadPage = useCallback(page => setPageOffload(page), []);
  const applyOffloadPagSize = useCallback((current, size) => setShowSizeOffload(size), []);

  const handleRemove = useCallback(() => {
    setLoader(true);
    updateTripSTO(
      {
        stoItemId,
        stopId: stop.id,
        isStoItemUnassigned: true,
      },
      trip.tripNumber
    )
      .then(() => {
        closeRemoveSto();
        setLoader(false);
        successMessage("STO was unassigned successfully");
        update();
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [trip, stop, stoItemId]);

  const handleRemoveInbound = useCallback(
    reason => {
      closeRemoveInboundSto();
      switch (reason) {
        case "unassign":
          setLoader(true);
          updateTripSTO(
            {
              stoItemId,
              stopId: stop.id,
              isStoItemUnassigned: true,
            },
            trip.tripNumber
          )
            .then(() => {
              closeRemoveSto();
              setLoader(false);
              successMessage("STO was unassigned successfully");
              update();
            })
            .catch(err => {
              setLoader(false);
              defaultNgiErrorMethod(err);
            });
          break;
        case "createNew":
          openEditSto(true);
          break;
        default:
          break;
      }
    },
    [trip, stop, stoItemId]
  );

  return (
    <>
      <Collapse className={classes.collapse} collapsible={loadedAssets?.length} expandIconPosition={"end"}>
        <Panel
          key={"panel"}
          className={classnames(classes.collapsePanel, isChanged && classes.changed)}
          extra={
            <div className={classes.extraContainer}>
              <div className={classes.viewAll}>View all assets</div>
              {isEdit &&
                userHasOneOfRoles([
                  USER_ROLES.SYSTEM_FULL_ADMINS,
                  USER_ROLES.PRODUCT_TECHNICIAN_LEAD,
                  USER_ROLES.IT_OPERATIONS,
                  USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                  USER_ROLES.WAREHOUSE,
                  USER_ROLES.FINANCE,
                ]) && (
                  <NGIDropdownButton
                    toggleEl={
                      <div className={classes.actions}>
                        <img src={menuIcon} alt="remove" />
                      </div>
                    }
                    menuItems={[
                      {
                        label: "Edit Order",
                        callback: openEditSto,
                      },
                      {
                        label: "Unassign Order",
                        disabled: !["Pending", "Planned", "InTransit"].includes(trip.status),
                        callback: openRemoveSto,
                      },
                    ]}
                  />
                )}
            </div>
          }
          showArrow={false}
          header={
            <div className={classnames(classes.assetItem, isChanged && classes.changed)}>
              <div className={classes.iconContainer}>
                <div className={classes.icon}>
                  <img
                    className={stoType === STO_TYPES.INBOUND ? classes.inbound : classes.outbound}
                    src={stoType === "Inbound" ? importIcon : exportIcon}
                    alt="sto type"
                  />
                </div>
                <div>
                  <CustomLink className={classes.link} onClick={openDetails}>
                    {`${group} (${stoId})`}
                  </CustomLink>
                </div>
              </div>
              <div className={classes.rightContainer}>
                <div className={classes.displayFlex}>
                  <span className={classes.subtitle}>Condition</span>
                  <b>{textFormatter(condition)}</b>
                </div>
                <div className={classes.displayFlex}>
                  <span className={classes.subtitle}>QTY</span>
                  <b>{textFormatter(quantity)}</b>
                </div>
              </div>
            </div>
          }
          style={{ padding: 0, margin: 0 }}
        >
          <h6>Loaded assets ({loadedAssetsCount}):</h6>
          {paginatedLoadedAssets?.length ? (
            <>
              {paginatedLoadedAssets?.map(item => (
                <LoadedAssetItem key={item.id} item={item} />
              ))}
              <Pagination
                onChange={applyPage}
                showSizeChanger
                onShowSizeChange={applyPagSize}
                defaultPageSize={showSize}
                defaultCurrent={page}
                total={loadedAssetsCount || 0}
              />
            </>
          ) : (
            <div className={classes.description}>
              <p>No loaded assets</p>
            </div>
          )}
          <Divider />

          <h6>Offloaded assets ({offloadedAssetsCount}):</h6>
          {paginatedOffloadedAssets?.length ? (
            <>
              {paginatedOffloadedAssets?.map(item => (
                <LoadedAssetItem key={item.id} item={item} />
              ))}
              <Pagination
                onChange={applyOffloadPage}
                showSizeChanger
                onShowSizeChange={applyOffloadPagSize}
                defaultPageSize={showSizeOffload}
                defaultCurrent={pageOffload}
                total={offloadedAssetsCount || 0}
              />
            </>
          ) : (
            <div className={classes.description}>
              <p>No offloaded assets</p>
            </div>
          )}
        </Panel>
      </Collapse>
      {isOpen && <STODetailsModal id={stoId} onClose={closeDetails} />}
      {!!isEditOpenType && (
        <EditSTOModal
          createType={isEditOpenType}
          trip={trip}
          stop={stop}
          setChangedList={setChangedList}
          editId={stoItemId}
          onClose={closeEditSto}
          stoId={stoId}
          update={update}
        />
      )}

      {isRemoveOpen && (
        <ConfirmationModal
          loading={isLoading}
          title="Confirm Unassigning STO"
          description="Are you sure you want to unassign this STO?"
          confirmBtnTitle="Confirm"
          cancelBtnTitle="Cancel"
          onCancel={closeRemoveSto}
          onConfirm={handleRemove}
        />
      )}
      {isInboundRemoveOpen && (
        <ConfirmationUnassignModal
          loading={isLoading}
          onCancel={closeRemoveInboundSto}
          onConfirm={handleRemoveInbound}
        />
      )}
      {isOutboundUnassignOpen && (
        <ConfirmationModal
          title="Asset Deduction"
          cancelBtnTitle="Cancel"
          confirmBtnTitle="Continue"
          description={`Please select an STO you want to deduct ${quantity} ${condition} ${group}(s) in order to complete the STO change.`}
          onCancel={closeOutboundUnassignSto}
          onConfirm={onConfirmOutboundUnassign}
        />
      )}
      {isInboundUnassignOpen && (
        <ConfirmationUpdateModal
          title="Confirm Updating STO"
          onCancel={closeInboundUnassignSto}
          onConfirm={onConfirmInboundUnassign}
          updateReasons={UPDATE_REASONS[TRIP_STATUSES.IN_TRANSIT][STO_TYPES.INBOUND]}
        />
      )}
    </>
  );
};

export default EditAssetItem;
